import { FC, useCallback, useMemo } from 'react'

import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'

import { ESP_TRANSLATION_KEYS } from '../../../configs/translations'
import { getEspPartnerPageLink } from '../../../lib/getEspPartnerPageLink'
import { IEspRequest } from '../../../model/types'
import { EspBadge } from '../../espBadge/EspBadge'
import { REQUEST_STATUS_LABEL_KEYS, REQUEST_STATUS_TO_BADGE } from '../helpers/requestStatusBadge'

import styles from './EspRequestDetail.module.scss'

const KEYS = ESP_TRANSLATION_KEYS.admin
const FORM_KEYS = ESP_TRANSLATION_KEYS.requestForm

interface IEspRequestDetailProps {
  request: IEspRequest
  isResolving: boolean
  onApprove: () => void
  onReject: () => void
  onDelete: () => void
}

interface IDetailFieldProps {
  labelKey: string
  value: string
}

const DetailField = ({ labelKey, value }: IDetailFieldProps) => (
  <div className={styles.field}>
    <span className={styles.fieldLabel}>
      <Translation text={labelKey} span />
    </span>
    <span className={styles.fieldValue}>{value}</span>
  </div>
)

/** The selected request's full card: meta, both form sections, and the decision actions. */
export const EspRequestDetail: FC<IEspRequestDetailProps> = ({
  request,
  isResolving,
  onApprove,
  onReject,
  onDelete,
}) => {
  const { sponsor } = request
  const isPending = request.status === 'pending'
  const { accountInfo, additionalInfo } = request

  const titleReplaceParams = useMemo(
    () => [{ key: '{partner}', value: accountInfo.partnerName }],
    [accountInfo.partnerName]
  )

  const requestedByReplaceParams = useMemo(
    () => [{ key: '{name}', value: request.requestedBy || '—' }],
    [request.requestedBy]
  )

  // A new tab, so the admin keeps their place in the queue (same as the plan's own
  // actions menu does for partner-adjacent pages).
  const openPartnerPage = useCallback(() => {
    window.open(getEspPartnerPageLink(accountInfo.partnerId), '_blank', 'noreferrer')
  }, [accountInfo.partnerId])

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.titleGroup}>
          <div className={styles.title}>
            <Translation text={KEYS.detailTitle} replaceParams={titleReplaceParams} span />
          </div>

          <Button
            variant="text"
            color="primary"
            size="sm"
            iconButton="kbase kbase-external-link"
            onClick={openPartnerPage}
          />
        </div>

        {isPending ? (
          <div className={styles.actions}>
            <Button color="danger" disabled={isResolving} onClick={onDelete}>
              <Translation text={KEYS.actionDelete} />
            </Button>
            <Button color="danger" disabled={isResolving} onClick={onReject}>
              <Translation text={KEYS.actionReject} />
            </Button>
            <Button variant="contained" color="primary" disabled={isResolving} onClick={onApprove}>
              <Translation text={KEYS.actionApprove} />
            </Button>
          </div>
        ) : null}
      </div>

      <div className={styles.meta}>
        <EspBadge
          variant="neutral"
          labelKey={request.type === 'change' ? KEYS.typeChange : KEYS.typeEnrollment}
        />
        <EspBadge
          variant={REQUEST_STATUS_TO_BADGE[request.status]}
          labelKey={REQUEST_STATUS_LABEL_KEYS[request.status]}
        />

        <span className={styles.sponsor}>
          <span>{sponsor.name}</span>
          {sponsor.email ? <span className={styles.sponsorTitle}> · {sponsor.email}</span> : null}
        </span>

        <span className={styles.requestedBy}>
          <Translation
            text={KEYS.detailRequestedBy}
            replaceParams={requestedByReplaceParams}
            span
          />
          {' · '}
          {new Date(request.createdAt).toLocaleDateString()}
        </span>
      </div>

      {request.type === 'change' || request.justification ? (
        <div className={styles.justification}>
          <span className={styles.fieldLabel}>
            <Translation text={FORM_KEYS.justification} span />
          </span>
          <span>{request.justification || '—'}</span>
        </div>
      ) : null}

      {request.rejectionReason ? (
        <div className={styles.rejectionReason}>
          <span className={styles.fieldLabel}>
            <Translation text={KEYS.rejectionReason} span />
          </span>
          <span>{request.rejectionReason}</span>
        </div>
      ) : null}

      <div className={styles.sectionTitle}>
        <Translation text={FORM_KEYS.accountSectionTitle} span />
      </div>

      <div className={styles.grid}>
        <DetailField labelKey={FORM_KEYS.partnerName} value={accountInfo.partnerName} />
        <DetailField labelKey={FORM_KEYS.partnerId} value={accountInfo.partnerId} />
        <DetailField labelKey={FORM_KEYS.partnerCountry} value={accountInfo.partnerCountry} />
        <DetailField
          labelKey={FORM_KEYS.accountClassification}
          value={accountInfo.accountClassification}
        />
        <DetailField labelKey={FORM_KEYS.accountSegment} value={accountInfo.accountSegment} />
      </div>

      <div className={styles.sectionTitle}>
        <Translation text={FORM_KEYS.additionalSectionTitle} span />
      </div>

      <div className={styles.grid}>
        <DetailField
          labelKey={FORM_KEYS.employeesCount}
          value={
            additionalInfo.employeesCount !== null
              ? additionalInfo.employeesCount.toLocaleString()
              : '—'
          }
        />
        <DetailField
          labelKey={FORM_KEYS.annualRevenue}
          value={
            additionalInfo.annualRevenueM !== null ? `$${additionalInfo.annualRevenueM}M` : '—'
          }
        />
        <DetailField labelKey={FORM_KEYS.accountValue} value={additionalInfo.accountValue || '—'} />
        <DetailField
          labelKey={FORM_KEYS.isBusinessGrowing}
          value={additionalInfo.isBusinessGrowing ? 'Yes' : 'No'}
        />
        <DetailField
          labelKey={FORM_KEYS.accountContact}
          value={additionalInfo.accountContact || '—'}
        />
      </div>

      <div className={styles.gridWide}>
        <DetailField
          labelKey={FORM_KEYS.missionAndVision}
          value={additionalInfo.missionAndVision || '—'}
        />
        <DetailField
          labelKey={FORM_KEYS.keyChallenges}
          value={additionalInfo.keyChallenges || '—'}
        />
        <DetailField
          labelKey={FORM_KEYS.productsAndSolutions}
          value={additionalInfo.productsAndSolutions || '—'}
        />
      </div>
    </div>
  )
}

EspRequestDetail.displayName = 'EspRequestDetail'
